import * as React from 'react';
import { useForm } from 'react-hook-form';
import { Typography } from "@mui/material";
import { getAuth, sendPasswordResetEmail } from 'firebase/auth';
import cx from 'classnames';
import ReactLoading from 'react-loading';
import { ENV } from '../../../config/env';
import { useFirebase } from '../../../hooks/useFirebase';
import { withReducerLogger } from '../../../utils/debugReducer';
import './ForgotPasswordForm.styles.scss';
import './Form.styles.scss';

type ForgotPasswordFormProps = { onCancel: () => void }
type ForgotPasswordInput = {
  email: string
}

type ForgotAction = | { type: 'loading' } | { type: 'error'; message: string } | { type: 'success' }

type ForgotState = { loading: boolean; error?: string; success: boolean }

const reducer = (state: ForgotState, action: ForgotAction): ForgotState => {
  switch (action.type) {
    case 'loading':
      return { ...state, loading: true, error: undefined }
    case 'error':
      return { ...state, loading: false, error: action.message }
    case 'success':
      return { ...state, loading: false, success: true }
    default:
      return state
  }
}

export const ForgotPasswordForm: React.FC<ForgotPasswordFormProps> = ({ onCancel, }) => {
  const { register, handleSubmit, setValue, formState: { errors }, } = useForm<ForgotPasswordInput>()
  const firebaseApp = useFirebase();
  const auth = firebaseApp ? getAuth(firebaseApp) : null; // Ensure auth is always defined
  const [state, dispatch] = React.useReducer(ENV.isDevelopment ? withReducerLogger(reducer) : reducer, {
    loading: false,
    success: false,
  })

  const onSubmit = async (values: ForgotPasswordInput) => {
    try {
      dispatch({ type: 'loading' })
      await sendPasswordResetEmail(auth!, values.email, { url: `${window.location.href}?email=${values.email}`, })
      setValue('email', '')
      dispatch({ type: 'success' })
      setTimeout(() => window.location.reload(), 5000)
    } catch (error: any) {
      console.warn(error)
      switch (error.code) {
        case 'auth/user-not-found':
          dispatch({
            type: 'error',
            message: 'An account with this email address doesn’t exist',
          })
          break
        case 'auth/invalid-email':
          dispatch({
            type: 'error',
            message: 'Please enter a valid email address',
          })
          break
        default:
          dispatch({ type: 'error', message: error.message })
          break
      }
    }
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="forgot-password__container" noValidate      >
      <Typography variant="h4" className="forgot-password__title" >Forgot password</Typography>
      <p className="forgot-password__intro">
        Enter the email you used to join Defeat Diabetes and we&apos;ll send
        you an email with details on how to reset your password.
      </p>

      <div className="control">
        <input type="email" id="email" placeholder="Email address"
          className={cx('input', { '--error': errors.email })}
          {...register('email', {
            required: 'This is a required field',
            pattern: { value: /.+@.+\..+/, message: 'Please enter a valid email address' },
          })}
        />
        {errors.email && <p className="control__warning">{errors.email.message}</p>}
      </div>
      {state.success ? (<p className="control__help">Please check your email for reset password link.</p>) : null}
      {state.error && <p className="control__warning">{state.error}</p>}
      <button className="forgot-password__action" type="submit" disabled={state.loading}>
        {state.loading ? (<ReactLoading type="spin" color="#ffffff" height={25} width={25} />) : ('Reset Password')}
      </button>
      <div>
        <button className="forgot-password__cancel" type="button" onClick={onCancel} disabled={state.loading}>
          Cancel
        </button>
      </div>
    </form>
  )
}
